// ==================== KOMPASS (Phase 1) ====================
// The core Work item engine: My Work (on me / on my team / I raised), Team Work, Raise, and the
// item detail (hand off, accept, close, cancel, comment). See KOMPASS_DESIGN.md and
// KOMPASS_MASTER_PLAN.md. Types/steps/Guruvak/broadcasts/recurrence are later phases — Phase 1
// items are always a single hand-off chain, no steps.
import { state } from '../state.js';
import { showToast, showConfirm } from '../components/toasts.js';
import { ensureStyle } from './gurukul-consent-flow.js'; // shared bordered-row look (.gk-ci), not Gurukul-specific

function esc(text) {
  return String(text ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
async function api(path, options = {}) {
  const res = await fetch(path, { credentials: 'include', headers: { 'Content-Type': 'application/json' }, ...options });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
}
const post = (path, body) => api(path, { method: 'POST', body: JSON.stringify(body) });
const fmtDate = d => d ? new Date(d).toLocaleDateString() : '';
const fmtDateTime = d => d ? new Date(d).toLocaleString() : '';

const STATUS_LABEL = { open: 'Open', wip: 'In progress', closed: 'Closed', cancelled: 'Cancelled' };
const STATUS_BADGE = { open: 'badge-warning', wip: 'badge-info', closed: 'badge-success', cancelled: 'badge-danger' };
const badge = s => `<span class="badge ${STATUS_BADGE[s] || 'badge-info'}">${esc(STATUS_LABEL[s] || s)}</span>`;
const PRIORITY_BADGE = { high: 'badge-danger', medium: 'badge-warning', low: 'badge-info' };
const PRIORITY_LABEL = { high: 'High', medium: 'Medium', low: 'Low' };
const priorityBadge = p => p ? `<span class="badge ${PRIORITY_BADGE[p] || 'badge-info'}">${esc(PRIORITY_LABEL[p] || p)}</span>` : '';
const isOverdue = i => i.due_date && i.status !== 'closed' && i.status !== 'cancelled' && new Date(i.due_date) < new Date(new Date().toDateString());

let currentItem = null;
let raiseTeamMembers = [];

export function getKompassPage() {
  return `
    <h1 class="page-title">Kompass</h1>
    <p class="page-intro">Everything to do, flag or request — in one place. Raise it, hand it off, close it.</p>

    <div class="btn-group" id="kpTabs" style="display:flex;gap:8px;margin-bottom:16px;flex-wrap:wrap;">
      <button type="button" class="primary" data-tab="dashboard">Dashboard</button>
      <button type="button" class="secondary" data-tab="mine">My Work</button>
      <button type="button" class="secondary" data-tab="team">Team Work</button>
      <button type="button" class="secondary" data-tab="raise">Raise</button>
    </div>

    <div id="kpTabDashboard">
      <div class="card" id="kpDashOverdue" style="display:none;">
        <h2>⚠️ Overdue</h2>
        <div id="kpDashOverdueList"></div>
      </div>
      <div class="card">
        <h2>What needs attention</h2>
        <div id="kpDashByType"><p class="empty-state">Loading…</p></div>
      </div>
    </div>

    <div id="kpTabMine" style="display:none;">
      <div class="card filter-card">
        <div class="form-grid-row form-grid-row--portal-filters">
          <div class="form-group form-group--narrow"><label for="kpMineType">Type</label><select id="kpMineType"><option value="">All types</option></select></div>
          <div class="form-group form-group--narrow"><label for="kpMinePriority">Priority</label><select id="kpMinePriority"><option value="">Any</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option></select></div>
          <div class="form-group form-group--narrow"><label for="kpMineFrom">Raised from</label><input type="date" id="kpMineFrom"></div>
          <div class="form-group form-group--narrow"><label for="kpMineTo">Raised to</label><input type="date" id="kpMineTo"></div>
          <div class="form-group form-group--search"><label for="kpMineQ">Search</label><input type="text" id="kpMineQ" placeholder="Title or item number" maxlength="80"></div>
          <div class="form-group form-group--action"><label>&nbsp;</label><button type="button" id="kpMineFilterBtn">Search</button></div>
        </div>
      </div>
      <div class="card">
        <h2>On me <span id="kpCountMe" class="badge badge-info"></span></h2>
        <div id="kpListMe"><p class="empty-state">Loading…</p></div>
      </div>
      <div class="card">
        <h2>On my team <span id="kpCountTeam" class="badge badge-info"></span></h2>
        <p class="page-intro">Items held by a team you belong to. If your team leads it, you are accountable for it.</p>
        <div id="kpListMyTeam"><p class="empty-state">Loading…</p></div>
      </div>
      <div class="card">
        <h2>I raised <span id="kpCountRaised" class="badge badge-info"></span></h2>
        <p class="page-intro">Items you started that are now waiting on someone else.</p>
        <div id="kpListRaised"><p class="empty-state">Loading…</p></div>
      </div>
      <div class="card">
        <h2>Watching <span id="kpCountWatching" class="badge badge-info"></span></h2>
        <p class="page-intro">Items you were added to so you can follow them (for example, a learner's LMS account request).</p>
        <div id="kpListWatching"><p class="empty-state">Loading…</p></div>
      </div>
    </div>

    <div id="kpTabTeam" style="display:none;">
      <div class="card filter-card">
        <div class="form-grid-row form-grid-row--portal-filters">
          <div class="form-group"><label for="kpTeamSelect">Team</label><select id="kpTeamSelect"><option value="">Everything I can reach</option></select></div>
          <div class="form-group form-group--narrow"><label for="kpFilterType">Type</label><select id="kpFilterType"><option value="">All types</option></select></div>
          <div class="form-group form-group--narrow">
            <label for="kpTeamStatus">Status</label>
            <select id="kpTeamStatus">
              <option value="">Open + in progress</option>
              <option value="open">Open</option>
              <option value="wip">In progress</option>
              <option value="closed">Closed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div class="form-group"><label for="kpFilterAssigned">Assigned to</label><input type="text" id="kpFilterAssigned" list="kpAssignedList" placeholder="Name or email" autocomplete="off"><datalist id="kpAssignedList"></datalist></div>
          <div class="form-group form-group--narrow"><label for="kpFilterFrom">Raised from</label><input type="date" id="kpFilterFrom"></div>
          <div class="form-group form-group--narrow"><label for="kpFilterTo">Raised to</label><input type="date" id="kpFilterTo"></div>
          <div class="form-group form-group--search"><label for="kpFilterQ">Search</label><input type="text" id="kpFilterQ" placeholder="Title or item number" maxlength="80"></div>
          <div class="form-group form-group--action"><label>&nbsp;</label><button type="button" id="kpFilterBtn">Search</button></div>
        </div>
      </div>
      <div class="card"><h2>Work items</h2><div id="kpListTeam"><p class="empty-state">Loading…</p></div></div>
    </div>

    <div id="kpTabRaise" style="display:none;">
      <div class="card">
        <h2>Raise a work item</h2>
        <form id="kpRaiseForm" class="form-stack" autocomplete="off">
          <div class="form-grid-row">
            <div class="form-group"><label for="kpType">Type <span class="req">*</span></label><select id="kpType" required></select></div>
            <div class="form-group"><label for="kpTeam">Team <span class="req">*</span></label><select id="kpTeam" required></select></div>
            <div class="form-group"><label for="kpPriority">Priority</label><select id="kpPriority"><option value="low">Low</option><option value="medium" selected>Medium</option><option value="high">High</option></select></div>
            <div class="form-group"><label for="kpDueDate">Due date</label><input type="date" id="kpDueDate"></div>
          </div>
          <div class="form-group">
            <label>Assign to <span class="req">*</span></label>
            <div class="btn-group" style="display:flex;gap:8px;flex-wrap:wrap;">
              <label class="gk-ci" style="cursor:pointer;"><input type="radio" name="kpHolderKind" value="me" checked> <span>Myself</span></label>
              <label class="gk-ci" style="cursor:pointer;"><input type="radio" name="kpHolderKind" value="person"> <span>Someone in this team</span></label>
              <label class="gk-ci" style="cursor:pointer;"><input type="radio" name="kpHolderKind" value="team"> <span>The team (any member picks it up; the lead is accountable)</span></label>
            </div>
          </div>
          <div class="form-group" id="kpHolderPersonGroup" style="display:none;">
            <label for="kpHolderPerson">Person</label>
            <select id="kpHolderPerson"></select>
          </div>
          <div class="form-group"><label for="kpTitle">Title <span class="req">*</span></label><input type="text" id="kpTitle" maxlength="200" required style="max-width:560px;width:100%;"></div>
          <div class="form-group"><label for="kpDesc">Description</label><textarea id="kpDesc" rows="5" maxlength="5000" style="max-width:760px;width:100%;"></textarea></div>
          <button type="submit" id="kpRaiseBtn">Raise</button>
        </form>
      </div>
    </div>

    <div id="kpDetailModal" class="modal">
      <div class="modal-content" style="max-width:760px;">
        <button type="button" class="close-modal" id="kpDetailClose">&times;</button>
        <h2 id="kpDetailTitle">Work item</h2>
        <div id="kpDetailBody"><p class="empty-state">Loading…</p></div>
        <div id="kpDetailFooter" class="btn-group" style="margin-top:16px;display:flex;gap:8px;justify-content:flex-end;flex-wrap:wrap;"></div>
      </div>
    </div>

    <div id="kpSubtaskModal" class="modal">
      <div class="modal-content" style="max-width:520px;padding:22px 26px;">
        <h2>Add a subtask</h2>
        <p class="page-intro">The parent item stays where it is — this becomes its own item, held by whoever you pick.</p>
        <div class="form-group"><label for="kpSubTeam">Team</label><select id="kpSubTeam"></select></div>
        <div class="form-group">
          <label>Assign to <span class="req">*</span></label>
          <div class="btn-group" style="display:flex;gap:8px;flex-wrap:wrap;">
            <label class="gk-ci" style="cursor:pointer;"><input type="radio" name="kpSubHolderKind" value="person" checked> <span>A person</span></label>
            <label class="gk-ci" style="cursor:pointer;"><input type="radio" name="kpSubHolderKind" value="team"> <span>A team</span></label>
          </div>
        </div>
        <div class="form-group" id="kpSubPersonGroup"><label for="kpSubPerson">Person</label><select id="kpSubPerson"></select></div>
        <div class="form-group" id="kpSubTeamHolderGroup" style="display:none;"><label for="kpSubTeamHolder">Team</label><select id="kpSubTeamHolder"></select></div>
        <div class="form-group"><label for="kpSubTitle">Title <span class="req">*</span></label><input type="text" id="kpSubTitle" maxlength="200"></div>
        <div class="form-group"><label for="kpSubDesc">Description</label><textarea id="kpSubDesc" rows="3" maxlength="5000"></textarea></div>
        <div class="form-grid-row" style="display:flex;gap:10px;">
          <div class="form-group" style="flex:1;"><label for="kpSubPriority">Priority</label><select id="kpSubPriority"><option value="low">Low</option><option value="medium" selected>Medium</option><option value="high">High</option></select></div>
          <div class="form-group" style="flex:1;"><label for="kpSubDue">Due date</label><input type="date" id="kpSubDue"></div>
        </div>
        <div class="btn-group" style="display:flex;gap:8px;justify-content:flex-end;">
          <button type="button" class="secondary" id="kpSubCancel">Cancel</button>
          <button type="button" id="kpSubConfirm">Add subtask</button>
        </div>
      </div>
    </div>

    <div id="kpEditModal" class="modal">
      <div class="modal-content" style="max-width:520px;padding:22px 26px;">
        <h2>Edit</h2>
        <div class="form-group"><label for="kpEditTitle">Title <span class="req">*</span></label><input type="text" id="kpEditTitle" maxlength="200"></div>
        <div class="form-group"><label for="kpEditDesc">Description</label><textarea id="kpEditDesc" rows="4" maxlength="5000"></textarea></div>
        <div class="form-grid-row" style="display:flex;gap:10px;">
          <div class="form-group" style="flex:1;"><label for="kpEditPriority">Priority</label><select id="kpEditPriority"><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option></select></div>
          <div class="form-group" style="flex:1;"><label for="kpEditDue">Due date</label><input type="date" id="kpEditDue"></div>
        </div>
        <div class="btn-group" style="display:flex;gap:8px;justify-content:flex-end;">
          <button type="button" class="secondary" id="kpEditCancel">Cancel</button>
          <button type="button" id="kpEditConfirm">Save</button>
        </div>
      </div>
    </div>

    <div id="kpHandoffModal" class="modal">
      <div class="modal-content" style="max-width:480px;padding:22px 26px;">
        <h2>Hand off</h2>
        <div class="form-group">
          <label>Give this to</label>
          <div class="btn-group" style="display:flex;gap:8px;">
            <label class="gk-ci" style="cursor:pointer;"><input type="radio" name="kpHoTarget" value="person" checked> <span>A person</span></label>
            <label class="gk-ci" style="cursor:pointer;"><input type="radio" name="kpHoTarget" value="team"> <span>A team</span></label>
          </div>
        </div>
        <div class="form-group" id="kpHoPersonGroup">
          <label for="kpHoPerson">Person</label><select id="kpHoPerson"></select>
          <input type="text" id="kpHoPersonSearch" placeholder="Not listed? Search by name or email…" autocomplete="off" style="margin-top:6px;">
        </div>
        <div class="form-group" id="kpHoTeamGroup" style="display:none;"><label for="kpHoTeam">Team</label><select id="kpHoTeam"></select></div>
        <div class="form-group"><label for="kpHoNote">Note <span class="req">*</span></label><textarea id="kpHoNote" rows="3" maxlength="1000"></textarea></div>
        <div class="btn-group" style="display:flex;gap:8px;justify-content:flex-end;">
          <button type="button" class="secondary" id="kpHoCancel">Cancel</button>
          <button type="button" id="kpHoConfirm">Hand off</button>
        </div>
      </div>
    </div>

    <div id="kpAccountModal" class="modal">
      <div class="modal-content" style="max-width:480px;padding:22px 26px;">
        <h2>Record account &amp; close</h2>
        <p class="page-intro">Enter the login details you created. The password is stored encrypted — only <strong id="kpAccFor"></strong> can see it, and never in a message.</p>
        <div class="form-group"><label for="kpAccLogin">Login ID <span class="req">*</span></label><input type="text" id="kpAccLogin" maxlength="200" autocomplete="off"></div>
        <div class="form-group"><label for="kpAccPw">Temporary password <span class="req">*</span></label><input type="text" id="kpAccPw" maxlength="128" autocomplete="off"></div>
        <div class="form-group"><label for="kpAccNote">Note (optional)</label><textarea id="kpAccNote" rows="2" maxlength="1000"></textarea></div>
        <div class="btn-group" style="display:flex;gap:8px;justify-content:flex-end;">
          <button type="button" class="secondary" id="kpAccCancel">Cancel</button>
          <button type="button" class="success" id="kpAccConfirm">Record &amp; close</button>
        </div>
      </div>
    </div>

    <div id="kpNoteModal" class="modal">
      <div class="modal-content" style="max-width:480px;padding:22px 26px;">
        <h2 id="kpNoteTitle"></h2>
        <div class="form-group"><label for="kpNoteText" id="kpNoteHint"></label><textarea id="kpNoteText" rows="3" maxlength="1000"></textarea></div>
        <div class="btn-group" style="display:flex;gap:8px;justify-content:flex-end;">
          <button type="button" class="secondary" id="kpNoteCancel">Cancel</button>
          <button type="button" id="kpNoteConfirm">Confirm</button>
        </div>
      </div>
    </div>
  `;
}

function renderList(containerId, items, emptyText) {
  const el = document.getElementById(containerId);
  if (!items.length) { el.innerHTML = `<p class="empty-state">${esc(emptyText)}</p>`; return; }
  el.innerHTML = `
    <div class="table-container show-desktop">
      <table class="table-stack-mobile">
        <thead><tr><th>Item</th><th>Title</th><th>Team</th><th>Holder</th><th>Priority</th><th>Due</th><th>Status</th></tr></thead>
        <tbody>${items.map(i => `
          <tr class="row-clickable" data-id="${i.id}">
            <td data-label="Item"><strong>${esc(i.item_no)}</strong></td>
            <td data-label="Title">${esc(i.title)}</td>
            <td data-label="Team">${esc(i.team_name)}</td>
            <td data-label="Holder">${esc(i.holder_name)}${i.holder_type === 'team' ? ' (team)' : ''}</td>
            <td data-label="Priority">${priorityBadge(i.priority)}</td>
            <td data-label="Due"${isOverdue(i) ? ' style="color:var(--danger,#dc2626);font-weight:700;"' : ''}>${fmtDate(i.due_date) || '—'}</td>
            <td data-label="Status">${badge(i.status)}</td>
          </tr>`).join('')}</tbody>
      </table>
    </div>
    <div class="show-mobile data-card-list">
      ${items.map(i => `
        <article class="data-card data-card--compact data-card--clickable" data-id="${i.id}">
          <div class="data-card-top"><span class="data-card-title">${esc(i.item_no)}</span>${badge(i.status)}</div>
          <div class="data-card-row"><span>Title</span><span>${esc(i.title)}</span></div>
          <div class="data-card-row"><span>Team</span><span>${esc(i.team_name)}</span></div>
          <div class="data-card-row"><span>Holder</span><span>${esc(i.holder_name)}${i.holder_type === 'team' ? ' (team)' : ''}</span></div>
          <div class="data-card-row"><span>Priority</span><span>${priorityBadge(i.priority)}</span></div>
          <div class="data-card-row"><span>Due</span><span${isOverdue(i) ? ' style="color:var(--danger,#dc2626);font-weight:700;"' : ''}>${fmtDate(i.due_date) || '—'}</span></div>
        </article>`).join('')}
    </div>`;
  el.querySelectorAll('[data-id]').forEach(row => row.addEventListener('click', () => openDetail(row.dataset.id)));
}

function mineFilterParams() {
  const qs = new URLSearchParams();
  const type = document.getElementById('kpMineType')?.value;
  const priority = document.getElementById('kpMinePriority')?.value;
  const from = document.getElementById('kpMineFrom')?.value;
  const to = document.getElementById('kpMineTo')?.value;
  const q = document.getElementById('kpMineQ')?.value.trim();
  if (type) qs.set('type_code', type);
  if (priority) qs.set('priority', priority);
  if (from) qs.set('date_from', from);
  if (to) qs.set('date_to', to);
  if (q) qs.set('q', q);
  return qs.toString();
}

async function loadMyWork() {
  try {
    const extra = mineFilterParams();
    const suffix = extra ? '&' + extra : '';
    const [me, team, raised, watching] = await Promise.all([
      api('/api/kompass/items?scope=on_me' + suffix),
      api('/api/kompass/items?scope=on_team' + suffix),
      api('/api/kompass/items?scope=raised' + suffix),
      api('/api/kompass/items?scope=watching' + suffix),
    ]);
    document.getElementById('kpCountMe').textContent = me.items.length;
    document.getElementById('kpCountTeam').textContent = team.items.length;
    document.getElementById('kpCountRaised').textContent = raised.items.length;
    document.getElementById('kpCountWatching').textContent = watching.items.length;
    renderList('kpListMe', me.items, 'Nothing matches on you right now.');
    renderList('kpListMyTeam', team.items, 'Nothing matches on your teams right now.');
    renderList('kpListRaised', raised.items, "Nothing matches what you've raised.");
    renderList('kpListWatching', watching.items, 'You are not watching anything right now.');
  } catch (err) {
    showToast(err.message, 'error');
  }
}

async function loadDashboard() {
  try {
    const d = await api('/api/kompass/dashboard');
    const overdueCard = document.getElementById('kpDashOverdue');
    if (d.overdue_items.length) {
      overdueCard.style.display = '';
      document.getElementById('kpDashOverdueList').innerHTML = `
        <div class="table-container"><table class="table-stack-mobile"><tbody>
          ${d.overdue_items.map(i => `
            <tr class="row-clickable" data-id="${i.id}">
              <td data-label="Item"><strong>${esc(i.item_no)}</strong></td>
              <td data-label="Title">${esc(i.title)}</td>
              <td data-label="Type">${esc(i.type_name)}</td>
              <td data-label="Was due" style="color:var(--danger,#dc2626);font-weight:700;">${fmtDate(i.due_date)}</td>
            </tr>`).join('')}
        </tbody></table></div>`;
      document.querySelectorAll('#kpDashOverdueList [data-id]').forEach(row => row.addEventListener('click', () => openDetail(row.dataset.id)));
    } else {
      overdueCard.style.display = 'none';
    }
    const t = d.totals;
    document.getElementById('kpDashByType').innerHTML = `
      <div class="form-grid-row" style="display:flex;gap:12px;flex-wrap:wrap;margin-bottom:16px;">
        <div class="card" style="flex:1;min-width:140px;text-align:center;"><div style="font-size:1.8em;font-weight:700;">${t.on_me}</div><div class="page-intro" style="margin:0;">On me</div></div>
        <div class="card" style="flex:1;min-width:140px;text-align:center;"><div style="font-size:1.8em;font-weight:700;">${t.on_team}</div><div class="page-intro" style="margin:0;">On my team</div></div>
        <div class="card" style="flex:1;min-width:140px;text-align:center;"><div style="font-size:1.8em;font-weight:700;">${t.raised}</div><div class="page-intro" style="margin:0;">I raised</div></div>
        <div class="card" style="flex:1;min-width:140px;text-align:center;"><div style="font-size:1.8em;font-weight:700;${t.overdue ? 'color:var(--danger,#dc2626);' : ''}">${t.overdue}</div><div class="page-intro" style="margin:0;">Overdue</div></div>
      </div>
      ${d.by_type.length ? `
      <div class="table-container"><table class="table-stack-mobile">
        <thead><tr><th>Type</th><th>Open</th><th>In progress</th><th>Overdue</th></tr></thead>
        <tbody>${d.by_type.map(t2 => `
          <tr><td data-label="Type"><strong>${esc(t2.prefix)}</strong> ${esc(t2.name)}</td>
            <td data-label="Open">${t2.open}</td><td data-label="In progress">${t2.wip}</td>
            <td data-label="Overdue"${t2.overdue ? ' style="color:var(--danger,#dc2626);font-weight:700;"' : ''}>${t2.overdue}</td></tr>`).join('')}</tbody>
      </table></div>` : '<p class="empty-state">Nothing needs your attention right now.</p>'}`;
  } catch (err) {
    showToast(err.message, 'error');
  }
}

// Assigned-to search: a text box + datalist backed by /api/kompass/contacts (team roster first,
// then anyone else the caller can reach — same rule as Konnect messaging). Keeps a label->id map
// since a <datalist> only ever gives back the text the person typed or picked.
let assignedToMap = {};
let assignedToDebounce = null;
function personLabel(p) { return `${p.name || p.email}${p.email ? ' <' + p.email + '>' : ''}`; }

async function searchAssignedTo(q) {
  const teamId = document.getElementById('kpTeamSelect').value;
  const qs = new URLSearchParams();
  if (teamId) qs.set('team_id', teamId);
  if (q) qs.set('q', q);
  try {
    const { people } = await api('/api/kompass/contacts?' + qs.toString());
    assignedToMap = {};
    document.getElementById('kpAssignedList').innerHTML = people.map(p => {
      const label = personLabel(p);
      assignedToMap[label] = p.id;
      return `<option value="${esc(label)}">${p.in_team ? '(in team)' : '(reachable)'}</option>`;
    }).join('');
  } catch (err) { /* quiet — this is just an autocomplete helper */ }
}

async function loadSearch() {
  const teamId = document.getElementById('kpTeamSelect').value;
  const status = document.getElementById('kpTeamStatus').value;
  const typeCode = document.getElementById('kpFilterType').value;
  const assignedLabel = document.getElementById('kpFilterAssigned').value.trim();
  const dateFrom = document.getElementById('kpFilterFrom').value;
  const dateTo = document.getElementById('kpFilterTo').value;
  const q = document.getElementById('kpFilterQ').value.trim();
  const qs = new URLSearchParams({ mode: 'search' });
  if (teamId) qs.set('team_id', teamId);
  if (status) qs.set('status', status);
  if (typeCode) qs.set('type_code', typeCode);
  if (assignedLabel && assignedToMap[assignedLabel]) qs.set('assigned_to', assignedToMap[assignedLabel]);
  if (dateFrom) qs.set('date_from', dateFrom);
  if (dateTo) qs.set('date_to', dateTo);
  if (q) qs.set('q', q);
  try {
    const data = await api('/api/kompass/items?' + qs.toString());
    renderList('kpListTeam', data.items, 'Nothing matches these filters.');
  } catch (err) {
    showToast(err.message, 'error');
  }
}

function populateTeamSelects() {
  const opts = (state.teams || []).filter(t => !t.is_personal_team)
    .map(t => `<option value="${t.team_id}">${esc(t.team_name)}</option>`).join('');
  const teamSel = document.getElementById('kpTeamSelect');
  teamSel.innerHTML = '<option value="">Everything I can reach</option>' + opts;
  document.getElementById('kpTeam').innerHTML = opts;
  const first = (state.teams || []).find(t => !t.is_personal_team);
  if (first) document.getElementById('kpTeam').value = first.team_id;
}

async function loadRaiseTeamMembers(teamId) {
  try {
    const { people } = await api('/api/kompass/contacts?team_id=' + encodeURIComponent(teamId));
    raiseTeamMembers = people;
    document.getElementById('kpHolderPerson').innerHTML = people
      .map(m => `<option value="${m.id}">${esc(m.name || m.email)}${m.in_team ? '' : ' (outside this team, but you can reach them)'}</option>`).join('');
  } catch (err) { showToast(err.message, 'error'); }
}

// ───────────────────────────── Detail + actions ─────────────────────────────

async function openDetail(id) {
  const modal = document.getElementById('kpDetailModal');
  const body = document.getElementById('kpDetailBody');
  const footer = document.getElementById('kpDetailFooter');
  modal.classList.add('active');
  body.innerHTML = '<p class="empty-state">Loading…</p>';
  footer.innerHTML = '';
  let d;
  try { d = await api('/api/kompass/items?id=' + encodeURIComponent(id)); }
  catch (err) { body.innerHTML = `<p class="empty-state">${esc(err.message)}</p>`; return; }

  currentItem = d.item;
  document.getElementById('kpDetailTitle').innerHTML = `${esc(d.item.item_no)} — ${esc(d.item.title)} ${badge(d.item.status)}`;
  body.innerHTML = `
    ${d.parent ? `<p class="page-intro">Subtask of <a href="#" id="kpParentLink">${esc(d.parent.item_no)} — ${esc(d.parent.title)}</a></p>` : ''}
    <div class="table-container"><table class="table-stack-mobile"><tbody>
      <tr><th style="width:34%;text-align:left;">Type</th><td data-label="Type">${esc(d.item.type?.name || '')}</td></tr>
      <tr><th style="text-align:left;">Team</th><td data-label="Team">${esc(d.item.team_name)}</td></tr>
      <tr><th style="text-align:left;">Priority</th><td data-label="Priority">${priorityBadge(d.item.priority)}</td></tr>
      <tr><th style="text-align:left;">Due</th><td data-label="Due"${isOverdue(d.item) ? ' style="color:var(--danger,#dc2626);font-weight:700;"' : ''}>${fmtDate(d.item.due_date) || '—'}${isOverdue(d.item) ? ' (overdue)' : ''}</td></tr>
      <tr><th style="text-align:left;">Raised by</th><td data-label="Raised by">${esc(d.item.initiator_name)} on ${fmtDate(d.item.created_at)}</td></tr>
      <tr><th style="text-align:left;">Currently held by</th><td data-label="Held by">${esc(d.item.holder_name)}${d.item.holder_type === 'team' ? ' (team — the lead is accountable)' : ''}</td></tr>
      ${d.subtasks.length ? `<tr><th style="text-align:left;">Progress</th><td data-label="Progress"><div style="background:var(--border);border-radius:6px;overflow:hidden;height:18px;max-width:300px;"><div style="background:${d.item.progress === 100 ? '#2D6A4F' : '#D4A017'};width:${d.item.progress}%;height:100%;"></div></div> ${d.item.progress}% of ${d.subtasks.length} subtask${d.subtasks.length === 1 ? '' : 's'} done</td></tr>` : ''}
      ${d.item.description ? `<tr><th style="text-align:left;">Description</th><td data-label="Description" style="white-space:pre-wrap;">${esc(d.item.description)}</td></tr>` : ''}
      ${d.item.close_reason ? `<tr><th style="text-align:left;">Note</th><td data-label="Note">${esc(d.item.close_reason)}</td></tr>` : ''}
      ${d.is_account_request ? `<tr><th style="text-align:left;">Account details for</th><td data-label="Account details for">${esc(d.recipient_names.join(', ') || '—')} · ${d.account_recorded ? 'recorded (encrypted)' : 'not recorded yet'}
        ${d.is_recipient && d.account_recorded ? '<br><button type="button" class="secondary" id="kpBtnReveal" style="margin-top:6px;">Show login details</button><div id="kpRevealOut"></div>' : ''}</td></tr>` : ''}
    </tbody></table></div>

    <h3 style="margin-top:16px;">History</h3>
    ${d.handoffs.map(h => `
      <div style="border-left:3px solid var(--border);padding:4px 10px;margin-bottom:8px;">
        <strong>${esc(h.actor_name)}</strong> gave this to <strong>${esc(h.to_label)}</strong> · ${fmtDateTime(h.at)}
        ${h.note ? `<div>${esc(h.note)}</div>` : ''}
      </div>`).join('')}

    <h3 style="margin-top:16px;">Subtasks</h3>
    <div id="kpSubtaskList">${d.subtasks.length ? '' : '<p class="empty-state">No subtasks yet.</p>'}</div>
    ${d.can_add_subtask ? '<button type="button" class="secondary" id="kpAddSubtaskBtn" style="margin-top:6px;">+ Add subtask</button>' : ''}

    <h3 style="margin-top:16px;">Attachments</h3>
    <div id="kpAttachList"><p class="empty-state">Loading…</p></div>
    <div style="margin-top:8px;">
      <input type="file" id="kpAttachFile" style="display:none;">
      <button type="button" class="secondary" id="kpAttachBtn">📎 Attach a file</button>
    </div>

    <h3 style="margin-top:16px;">Comments</h3>
    ${d.comments.length ? d.comments.map(c => `
      <div style="border-left:3px solid ${c.is_internal ? '#D4A017' : 'var(--border)'};padding:4px 10px;margin-bottom:8px;${c.is_internal ? 'background:rgba(212,160,23,.08);' : ''}">
        <strong>${esc(c.author_name)}</strong>${c.is_internal ? ' <span class="badge badge-warning">Internal</span>' : ''} · ${fmtDateTime(c.created_at)}<div>${esc(c.body)}</div>
      </div>`).join('') : '<p class="empty-state">No comments yet.</p>'}
    <div class="form-group" style="margin-top:10px;">
      <textarea id="kpCommentText" rows="2" maxlength="3000" placeholder="Add a comment…" style="width:100%;max-width:640px;"></textarea>
      ${d.is_staff_side ? `<label class="gk-ci" style="cursor:pointer;margin-top:6px;max-width:340px;"><input type="checkbox" id="kpCommentInternal"> <span>Internal note — hidden from whoever raised this</span></label>` : ''}
      <button type="button" id="kpCommentBtn" style="margin-top:6px;">Comment</button>
    </div>`;

  document.getElementById('kpCommentBtn').addEventListener('click', () => addComment(d.item.id));
  document.getElementById('kpAttachBtn').addEventListener('click', () => document.getElementById('kpAttachFile').click());
  document.getElementById('kpAttachFile').addEventListener('change', e => { if (e.target.files[0]) uploadAttachment(d.item.id, e.target.files[0]); });
  loadAttachments(d.item.id);
  document.getElementById('kpParentLink')?.addEventListener('click', e => { e.preventDefault(); openDetail(d.parent.id); });
  if (d.subtasks.length) renderList('kpSubtaskList', d.subtasks, '');
  document.getElementById('kpAddSubtaskBtn')?.addEventListener('click', () => openSubtaskModal(d.item));

  const btns = [];
  const open = d.item.status === 'open', wip = d.item.status === 'wip';
  if (d.can_edit && (open || wip)) btns.push('<button type="button" class="secondary" id="kpBtnEdit">Edit</button>');
  if (d.can_hold && open) btns.push('<button type="button" class="success" id="kpBtnAccept">Accept &amp; start work</button>');
  if (d.can_hold && wip && d.is_account_request) btns.push('<button type="button" class="success" id="kpBtnRecord">Record account &amp; close</button>');
  if (d.can_hold && wip && !d.is_account_request) btns.push('<button type="button" class="success" id="kpBtnClose">Mark closed</button>');
  if (d.can_hold && (open || wip)) btns.push('<button type="button" id="kpBtnHandoff">Hand off</button>');
  if ((d.can_hold || d.item.initiator_id === state.user?.id) && (open || wip)) btns.push('<button type="button" class="danger" id="kpBtnCancel">Cancel</button>');
  btns.push('<button type="button" class="secondary" id="kpDetailClose2">Close</button>');
  footer.innerHTML = btns.join('');

  document.getElementById('kpDetailClose2').addEventListener('click', closeDetail);
  document.getElementById('kpBtnEdit')?.addEventListener('click', () => openEdit(d.item));
  document.getElementById('kpBtnAccept')?.addEventListener('click', acceptWork);
  document.getElementById('kpBtnClose')?.addEventListener('click', () => openNote('closed', 'Mark this closed', 'What was done? (optional)', false));
  document.getElementById('kpBtnCancel')?.addEventListener('click', () => openNote('cancelled', 'Cancel this item', 'Why is this being cancelled?', true));
  document.getElementById('kpBtnHandoff')?.addEventListener('click', openHandoff);
  document.getElementById('kpBtnRecord')?.addEventListener('click', () => openRecordAccount(d));
  document.getElementById('kpBtnReveal')?.addEventListener('click', async () => {
    if (!(await showConfirm('Show the login details? This view is recorded.'))) return;
    try {
      const s = await post('/api/kompass/items', { action: 'reveal_secret', id: d.item.id });
      document.getElementById('kpRevealOut').innerHTML = `<div style="margin-top:6px;">Login ID: <strong>${esc(s.login_id)}</strong><br>Temporary password: <code>${esc(s.password)}</code></div>`;
      document.getElementById('kpBtnReveal').style.display = 'none';
    } catch (err) { showToast(err.message, 'error'); }
  });
}

function openRecordAccount(d) {
  const modal = document.getElementById('kpAccountModal');
  document.getElementById('kpAccFor').textContent = d.recipient_names.join(', ') || 'the person it is for';
  ['kpAccLogin', 'kpAccPw', 'kpAccNote'].forEach(id => { document.getElementById(id).value = ''; });
  modal.classList.add('active');
  document.getElementById('kpAccCancel').onclick = () => modal.classList.remove('active');
  document.getElementById('kpAccConfirm').onclick = async () => {
    const login = document.getElementById('kpAccLogin').value.trim();
    const pw = document.getElementById('kpAccPw').value;
    if (!login || !pw) { showToast('Enter the login ID and the temporary password', 'error'); return; }
    if (!(await showConfirm('Record these login details and close this item?'))) return;
    try {
      await post('/api/kompass/items', { action: 'record_account', id: d.item.id, login_id: login, temp_password: pw, note: document.getElementById('kpAccNote').value.trim() || undefined });
      modal.classList.remove('active');
      showToast('Account recorded — the item is closed', 'success');
      await openDetail(d.item.id);
      await refreshActiveTab();
    } catch (err) { showToast(err.message, 'error'); }
  };
}

function openEdit(item) {
  document.getElementById('kpEditTitle').value = item.title || '';
  document.getElementById('kpEditDesc').value = item.description || '';
  document.getElementById('kpEditPriority').value = item.priority || 'medium';
  document.getElementById('kpEditDue').value = item.due_date ? item.due_date.slice(0, 10) : '';
  document.getElementById('kpEditModal').classList.add('active');
}

async function submitEdit() {
  const title = document.getElementById('kpEditTitle').value.trim();
  if (!title) { showToast('Give this a title', 'error'); return; }
  const ok = await showConfirm('Save these changes?');
  if (!ok) return;
  try {
    await post('/api/kompass/items', {
      action: 'update', id: currentItem.id, title,
      description: document.getElementById('kpEditDesc').value.trim(),
      priority: document.getElementById('kpEditPriority').value,
      due_date: document.getElementById('kpEditDue').value || null,
    });
    document.getElementById('kpEditModal').classList.remove('active');
    showToast('Saved', 'success');
    await openDetail(currentItem.id);
    await refreshActiveTab();
  } catch (err) { showToast(err.message, 'error'); }
}

function fmtSize(n) {
  if (!n) return '';
  if (n < 1024) return n + ' B';
  if (n < 1024 * 1024) return Math.round(n / 1024) + ' KB';
  return (n / (1024 * 1024)).toFixed(1) + ' MB';
}

async function loadAttachments(itemId) {
  const box = document.getElementById('kpAttachList');
  try {
    const { attachments } = await api('/api/kompass/attachments?item_id=' + encodeURIComponent(itemId));
    if (!attachments.length) { box.innerHTML = '<p class="empty-state">No files attached.</p>'; return; }
    box.innerHTML = attachments.map(a => `
      <div style="display:flex;align-items:center;gap:8px;padding:6px 0;border-bottom:1px solid var(--border);">
        <span style="flex:1;">📄 <a href="#" data-dl="${a.id}">${esc(a.file_name)}</a> <span class="page-intro" style="display:inline;margin:0;">(${fmtSize(a.size_bytes)}, ${esc(a.uploaded_by_name || a.uploaded_by_email)})</span></span>
        <button type="button" class="danger" data-del="${a.id}" style="padding:2px 10px;">Remove</button>
      </div>`).join('');
    box.querySelectorAll('[data-dl]').forEach(el => el.addEventListener('click', async e => {
      e.preventDefault();
      try {
        const { url } = await api('/api/kompass/attachments?download=' + el.dataset.dl);
        window.open(url, '_blank');
      } catch (err) { showToast(err.message, 'error'); }
    }));
    box.querySelectorAll('[data-del]').forEach(el => el.addEventListener('click', async () => {
      const ok = await showConfirm('Remove this attachment?');
      if (!ok) return;
      try {
        await post('/api/kompass/attachments', { action: 'delete', id: el.dataset.del });
        loadAttachments(itemId);
      } catch (err) { showToast(err.message, 'error'); }
    }));
  } catch (err) {
    box.innerHTML = `<p class="empty-state">${esc(err.message)}</p>`;
  }
}

async function uploadAttachment(itemId, file) {
  const input = document.getElementById('kpAttachFile');
  try {
    const init = await post('/api/kompass/attachments', {
      action: 'init', item_id: itemId, file_name: file.name, content_type: file.type, size_bytes: file.size });
    const putRes = await fetch(init.upload_url, { method: 'PUT', body: file, headers: { 'Content-Type': file.type || 'application/octet-stream' } });
    if (!putRes.ok) throw new Error('Upload to storage failed');
    await post('/api/kompass/attachments', {
      action: 'confirm', item_id: itemId, object_key: init.object_key, file_name: file.name, content_type: file.type, size_bytes: file.size });
    showToast('Attached', 'success');
    loadAttachments(itemId);
  } catch (err) { showToast(err.message, 'error'); }
  finally { input.value = ''; }
}

function closeDetail() { document.getElementById('kpDetailModal').classList.remove('active'); }

async function acceptWork() {
  const ok = await showConfirm(
    'You are formally accepting responsibility for this task. Moving it to "in progress" is your acceptance — confirm?');
  if (!ok) return;
  try {
    await post('/api/kompass/items', { action: 'status', id: currentItem.id, status: 'wip' });
    showToast('Accepted — now in progress', 'success');
    await openDetail(currentItem.id);
    await refreshActiveTab();
  } catch (err) { showToast(err.message, 'error'); }
}

function openNote(status, title, hint, required) {
  const modal = document.getElementById('kpNoteModal');
  document.getElementById('kpNoteTitle').textContent = title;
  document.getElementById('kpNoteHint').textContent = hint;
  const text = document.getElementById('kpNoteText');
  text.value = '';
  modal.classList.add('active');
  document.getElementById('kpNoteCancel').onclick = () => modal.classList.remove('active');
  document.getElementById('kpNoteConfirm').onclick = async () => {
    const note = text.value.trim();
    if (required && !note) { showToast('Please fill this in', 'error'); return; }
    const ok = await showConfirm('Are you sure?');
    if (!ok) return;
    try {
      await post('/api/kompass/items', { action: 'status', id: currentItem.id, status, note: note || undefined });
      modal.classList.remove('active');
      showToast(status === 'closed' ? 'Closed' : 'Cancelled', 'success');
      await openDetail(currentItem.id);
      await refreshActiveTab();
    } catch (err) { showToast(err.message, 'error'); }
  };
}

async function openHandoff() {
  const modal = document.getElementById('kpHandoffModal');
  document.getElementById('kpHoNote').value = '';
  document.getElementById('kpHoPersonSearch').value = '';
  document.querySelector('input[name="kpHoTarget"][value="person"]').checked = true;
  document.getElementById('kpHoPersonGroup').style.display = '';
  document.getElementById('kpHoTeamGroup').style.display = 'none';
  const { people } = await api('/api/kompass/contacts?team_id=' + encodeURIComponent(currentItem.team_id));
  document.getElementById('kpHoPerson').innerHTML = people
    .map(u => `<option value="${u.id}">${esc(u.name || u.email)}${u.in_team ? '' : ' (outside this team, but reachable)'}</option>`).join('');
  document.getElementById('kpHoTeam').innerHTML = (state.teams || []).filter(t => !t.is_personal_team)
    .map(t => `<option value="${t.team_id}">${esc(t.team_name)}</option>`).join('');
  document.querySelectorAll('input[name="kpHoTarget"]').forEach(r => r.onchange = () => {
    const isPerson = document.querySelector('input[name="kpHoTarget"]:checked').value === 'person';
    document.getElementById('kpHoPersonGroup').style.display = isPerson ? '' : 'none';
    document.getElementById('kpHoTeamGroup').style.display = isPerson ? 'none' : '';
  });
  modal.classList.add('active');
  document.getElementById('kpHoCancel').onclick = () => modal.classList.remove('active');
  document.getElementById('kpHoConfirm').onclick = async () => {
    const isPerson = document.querySelector('input[name="kpHoTarget"]:checked').value === 'person';
    const note = document.getElementById('kpHoNote').value.trim();
    if (!note) { showToast('Add a note for the hand-off', 'error'); return; }
    const ok = await showConfirm('Are you sure?');
    if (!ok) return;
    const body = { action: 'handoff', id: currentItem.id, note,
      holder_type: isPerson ? 'user' : 'team',
      holder_user_id: isPerson ? document.getElementById('kpHoPerson').value : undefined,
      holder_team_id: isPerson ? undefined : document.getElementById('kpHoTeam').value };
    try {
      await post('/api/kompass/items', body);
      modal.classList.remove('active');
      showToast('Handed off', 'success');
      await openDetail(currentItem.id);
      await refreshActiveTab();
    } catch (err) { showToast(err.message, 'error'); }
  };
}

// ───────────────────────────── Subtasks ─────────────────────────────

async function loadSubteamMembers(teamId) {
  try {
    const { people } = await api('/api/kompass/contacts?team_id=' + encodeURIComponent(teamId));
    document.getElementById('kpSubPerson').innerHTML = people
      .map(m => `<option value="${m.id}">${esc(m.name || m.email)}${m.in_team ? '' : ' (outside this team, but reachable)'}</option>`).join('');
  } catch (err) { showToast(err.message, 'error'); }
}

function openSubtaskModal(parentItem) {
  const opts = (state.teams || []).filter(t => !t.is_personal_team)
    .map(t => `<option value="${t.team_id}">${esc(t.team_name)}</option>`).join('');
  document.getElementById('kpSubTeam').innerHTML = opts;
  document.getElementById('kpSubTeamHolder').innerHTML = opts;
  document.getElementById('kpSubTeam').value = parentItem.team_id;
  document.getElementById('kpSubTitle').value = '';
  document.getElementById('kpSubDesc').value = '';
  document.getElementById('kpSubPriority').value = parentItem.priority || 'medium';
  document.getElementById('kpSubDue').value = '';
  document.querySelector('input[name="kpSubHolderKind"][value="person"]').checked = true;
  document.getElementById('kpSubPersonGroup').style.display = '';
  document.getElementById('kpSubTeamHolderGroup').style.display = 'none';
  loadSubteamMembers(document.getElementById('kpSubTeam').value);
  document.getElementById('kpSubtaskModal').classList.add('active');

  document.getElementById('kpSubTeam').onchange = e => loadSubteamMembers(e.target.value);
  document.querySelectorAll('input[name="kpSubHolderKind"]').forEach(r => r.onchange = () => {
    const isPerson = document.querySelector('input[name="kpSubHolderKind"]:checked').value === 'person';
    document.getElementById('kpSubPersonGroup').style.display = isPerson ? '' : 'none';
    document.getElementById('kpSubTeamHolderGroup').style.display = isPerson ? 'none' : '';
  });
  document.getElementById('kpSubCancel').onclick = () => document.getElementById('kpSubtaskModal').classList.remove('active');
  document.getElementById('kpSubConfirm').onclick = async () => {
    const title = document.getElementById('kpSubTitle').value.trim();
    if (!title) { showToast('Give this a title', 'error'); return; }
    const isPerson = document.querySelector('input[name="kpSubHolderKind"]:checked').value === 'person';
    const ok = await showConfirm('Add this subtask?');
    if (!ok) return;
    try {
      const { item } = await post('/api/kompass/items', {
        action: 'raise', parent_id: parentItem.id, team_id: document.getElementById('kpSubTeam').value,
        title, description: document.getElementById('kpSubDesc').value.trim() || undefined,
        priority: document.getElementById('kpSubPriority').value,
        due_date: document.getElementById('kpSubDue').value || undefined,
        holder_type: isPerson ? 'user' : 'team',
        holder_user_id: isPerson ? document.getElementById('kpSubPerson').value : undefined,
        holder_team_id: isPerson ? undefined : document.getElementById('kpSubTeamHolder').value,
      });
      document.getElementById('kpSubtaskModal').classList.remove('active');
      showToast(`Added subtask ${item.item_no}`, 'success');
      await openDetail(parentItem.id);
      await refreshActiveTab();
    } catch (err) { showToast(err.message, 'error'); }
  };
}

async function addComment(id) {
  const text = document.getElementById('kpCommentText');
  const bodyText = text.value.trim();
  if (!bodyText) return;
  const internal = !!document.getElementById('kpCommentInternal')?.checked;
  try {
    await post('/api/kompass/items', { action: 'comment', id, body: bodyText, internal });
    text.value = '';
    await openDetail(id);
  } catch (err) { showToast(err.message, 'error'); }
}

let activeTab = 'dashboard';
async function refreshActiveTab() {
  if (activeTab === 'dashboard') await loadDashboard();
  else if (activeTab === 'mine') await loadMyWork();
  else if (activeTab === 'team') await loadSearch();
}

// ───────────────────────────── Raise ─────────────────────────────

async function submitRaise(e) {
  e.preventDefault();
  const title = document.getElementById('kpTitle').value.trim();
  if (!title) { showToast('Give this a title', 'error'); return; }
  const holderKind = document.querySelector('input[name="kpHolderKind"]:checked').value;
  const teamId = document.getElementById('kpTeam').value;
  const body = {
    action: 'raise',
    type_code: document.getElementById('kpType').value,
    team_id: teamId,
    title,
    description: document.getElementById('kpDesc').value.trim() || undefined,
    priority: document.getElementById('kpPriority').value,
    due_date: document.getElementById('kpDueDate').value || undefined,
    holder_type: holderKind === 'team' ? 'team' : 'user',
    holder_user_id: holderKind === 'me' ? state.user?.id : holderKind === 'person' ? document.getElementById('kpHolderPerson').value : undefined,
    holder_team_id: holderKind === 'team' ? teamId : undefined,
  };
  const ok = await showConfirm('Raise this work item?');
  if (!ok) return;
  const btn = document.getElementById('kpRaiseBtn');
  btn.disabled = true;
  try {
    const { item } = await post('/api/kompass/items', body);
    showToast(`Raised ${item.item_no}`, 'success');
    document.getElementById('kpRaiseForm').reset();
    switchTab('mine');
    await loadMyWork();
  } catch (err) { showToast(err.message, 'error'); }
  finally { btn.disabled = false; }
}

function switchTab(tab) {
  activeTab = tab;
  document.querySelectorAll('#kpTabs [data-tab]').forEach(b => {
    b.className = b.dataset.tab === tab ? 'primary' : 'secondary';
  });
  document.getElementById('kpTabDashboard').style.display = tab === 'dashboard' ? '' : 'none';
  document.getElementById('kpTabMine').style.display = tab === 'mine' ? '' : 'none';
  document.getElementById('kpTabTeam').style.display = tab === 'team' ? '' : 'none';
  document.getElementById('kpTabRaise').style.display = tab === 'raise' ? '' : 'none';
  if (tab === 'dashboard') loadDashboard();
  else if (tab === 'team') loadSearch();
  else if (tab === 'mine') loadMyWork();
}

export async function initKompassPage() {
  ensureStyle();
  populateTeamSelects();
  document.querySelectorAll('#kpTabs [data-tab]').forEach(b => b.addEventListener('click', () => switchTab(b.dataset.tab)));
  document.getElementById('kpDetailClose').addEventListener('click', closeDetail);
  document.getElementById('kpTeamSelect').addEventListener('change', loadSearch);
  document.getElementById('kpTeamStatus').addEventListener('change', loadSearch);
  document.getElementById('kpFilterType').addEventListener('change', loadSearch);
  document.getElementById('kpFilterFrom').addEventListener('change', loadSearch);
  document.getElementById('kpFilterTo').addEventListener('change', loadSearch);
  document.getElementById('kpFilterBtn').addEventListener('click', loadSearch);
  document.getElementById('kpFilterQ').addEventListener('keydown', e => { if (e.key === 'Enter') loadSearch(); });
  document.getElementById('kpFilterAssigned').addEventListener('input', e => {
    clearTimeout(assignedToDebounce);
    assignedToDebounce = setTimeout(() => searchAssignedTo(e.target.value.trim()), 300);
  });
  document.getElementById('kpFilterAssigned').addEventListener('change', loadSearch); // fires when a datalist option is picked
  document.getElementById('kpRaiseForm').addEventListener('submit', submitRaise);
  document.querySelectorAll('input[name="kpHolderKind"]').forEach(r => r.addEventListener('change', () => {
    const kind = document.querySelector('input[name="kpHolderKind"]:checked').value;
    document.getElementById('kpHolderPersonGroup').style.display = kind === 'person' ? '' : 'none';
  }));
  document.getElementById('kpTeam').addEventListener('change', e => loadRaiseTeamMembers(e.target.value));
  document.getElementById('kpHoPersonSearch').addEventListener('input', e => {
    clearTimeout(assignedToDebounce);
    const q = e.target.value.trim();
    if (!q) return;
    assignedToDebounce = setTimeout(async () => {
      const { people } = await api('/api/kompass/contacts?q=' + encodeURIComponent(q));
      document.getElementById('kpHoPerson').innerHTML = people
        .map(u => `<option value="${u.id}">${esc(u.name || u.email)}</option>`).join('');
    }, 300);
  });

  document.getElementById('kpMineFilterBtn').addEventListener('click', loadMyWork);
  document.getElementById('kpMineType').addEventListener('change', loadMyWork);
  document.getElementById('kpMinePriority').addEventListener('change', loadMyWork);
  document.getElementById('kpMineFrom').addEventListener('change', loadMyWork);
  document.getElementById('kpMineTo').addEventListener('change', loadMyWork);
  document.getElementById('kpMineQ').addEventListener('keydown', e => { if (e.key === 'Enter') loadMyWork(); });
  document.getElementById('kpEditCancel').addEventListener('click', () => document.getElementById('kpEditModal').classList.remove('active'));
  document.getElementById('kpEditConfirm').addEventListener('click', submitEdit);

  try {
    const { types } = await api('/api/kompass/types');
    const opts = types.map(t => `<option value="${t.code}">${esc(t.name)}</option>`).join('');
    document.getElementById('kpType').innerHTML = opts;
    document.getElementById('kpFilterType').innerHTML = '<option value="">All types</option>' + opts;
    document.getElementById('kpMineType').innerHTML = '<option value="">All types</option>' + opts;
  } catch (err) { showToast(err.message, 'error'); }

  const firstTeam = document.getElementById('kpTeam').value;
  if (firstTeam) await loadRaiseTeamMembers(firstTeam);

  await loadDashboard();
}
